import { useMemo } from 'react';
import { useSelector } from '@xstate/store/react';
import { canvasSelectionStore } from '../stores/canvasSelectionStore';
import { useCanvasStores } from '../stores/CanvasContext';
import { getHandler } from '../entities/registry';
import type { ToolbarActionOption, ToolbarAction } from '../entities/EntityKindHandler';

export interface MergedToolbarAction {
  name: string;
  label: string;
  type: 'color' | 'select' | 'toggle';
  options: ToolbarActionOption[];
  values: unknown[];
  commonValue: unknown | undefined;
  applyAll: (value: unknown) => void;
  allowCustomInput?: boolean;
  customInputUnit?: string;
  customInputMin?: number;
  customInputMax?: number;
  customInputStep?: number;
}

export function useToolbarActions(): MergedToolbarAction[] {
  const { store } = useCanvasStores();
  const selectedEntityIds = useSelector(canvasSelectionStore, s => s.context.selectedEntityIds);

  // Build a stable key from selected entities' data — only changes when entity data
  // (not just positions) changes. This prevents re-running expensive getToolbarActions
  // on every drag frame.
  const selectedDataKey = useSelector(store, s => {
    if (selectedEntityIds.length === 0) return '';
    let key = '';
    for (const id of selectedEntityIds) {
      const e = s.context.nodes[id] ?? s.context.connectors[id] ?? s.context.groups[id];
      if (e) key += `${e.id}:${e.kind}:${JSON.stringify(e.data)}|`;
    }
    return key;
  });

  return useMemo(() => {
    if (selectedEntityIds.length === 0 || !selectedDataKey) return [];

    const ctx = store.getSnapshot().context;
    const allEntities = [
      ...Object.values(ctx.nodes),
      ...Object.values(ctx.connectors),
      ...Object.values(ctx.groups),
    ];
    const entityMap = new Map(allEntities.map(e => [e.id, e]));

    // Collect all actions from selected entities
    const actionsByName = new Map<string, { label: string; type: 'color' | 'select' | 'toggle'; actions: ToolbarAction[] }>();
    const nameOrder: string[] = [];

    for (const entityId of selectedEntityIds) {
      const entity = entityMap.get(entityId);
      if (!entity) continue;

      const handler = getHandler(entity.kind);
      const toolbarActions = handler.getToolbarActions({ store, entity });

      for (const action of toolbarActions) {
        let group = actionsByName.get(action.name);
        if (!group) {
          group = { label: action.label, type: action.type, actions: [] };
          actionsByName.set(action.name, group);
          nameOrder.push(action.name);
        }
        group.actions.push(action);
      }
    }

    // Merge into MergedToolbarActions
    const result: MergedToolbarAction[] = [];
    for (const name of nameOrder) {
      const group = actionsByName.get(name)!;

      // Deduplicate options by id (first occurrence wins)
      const seenOptionIds = new Set<string>();
      const mergedOptions: ToolbarActionOption[] = [];
      for (const action of group.actions) {
        if (action.options) {
          for (const opt of action.options) {
            if (!seenOptionIds.has(opt.id)) {
              seenOptionIds.add(opt.id);
              mergedOptions.push(opt);
            }
          }
        }
      }

      const values = group.actions.map(a => a.getValue());
      const allSame = values.length > 0 && values.every(v => v === values[0]);

      const firstAction = group.actions[0];
      result.push({
        name,
        label: group.label,
        type: group.type,
        options: mergedOptions,
        values,
        commonValue: allSame ? values[0] : undefined,
        applyAll: (value: unknown) => {
          for (const action of group.actions) {
            action.apply(value);
          }
        },
        allowCustomInput: firstAction.allowCustomInput,
        customInputUnit: firstAction.customInputUnit,
        customInputMin: firstAction.customInputMin,
        customInputMax: firstAction.customInputMax,
        customInputStep: firstAction.customInputStep,
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataKey, store]);
}
